import { Combobox as ComboboxBase } from "@headlessui/react";
import { cn } from "@mozioinc/ui";
import { useRef } from "react";
import { FaCaretDown, FaTimes } from "react-icons/fa";
import { Label } from "./label";
import { ListBox } from "./list-box";
import { Loading } from "./loading";
import type { ReactNode } from "react";

export type ComboboxProps = {
  options: string[];
  label: string;
  value: string;
  openOnFocus?: boolean;
  placeholder?: string;
  noOptionsText?: ReactNode;
  loading?: boolean;
  className?: string;
  name?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  onOpen?: () => void;
};

// a hack to set "search" as input id to disable 1Password (https://1password.community/discussion/117501/as-a-web-developer-how-can-i-disable-1password-filling-for-a-specific-field)
const handleRef = (ref: HTMLDivElement) => {
  ref?.querySelector("input[role=combobox]")?.setAttribute("id", "search");
};

export const Combobox = (props: ComboboxProps) => {
  const {
    options,
    label,
    value,
    className = "",
    name,
    openOnFocus = false,
    placeholder,
    noOptionsText = "No options",
    loading = false,
    inputRef,
    onBlur,
    onChange = () => {},
    onInputChange = () => {},
    onOpen = () => {},
  } = props;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClear = () => {
    onChange("");
    onInputChange("");
  };

  // solution from here: https://github.com/tailwindlabs/headlessui/discussions/1236#discussioncomment-2988166
  // TODO: remove when headlessui supports this feature.
  const handleInputFocus = (open: boolean) =>
    openOnFocus && !open && buttonRef.current!.click();

  return (
    <div
      ref={handleRef}
      className={`relative w-72 text-sm font-medium text-slate-700 ${className}`}
    >
      <ComboboxBase name={name} value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <div className="grid">
              <ComboboxBase.Label as={Label}>{label}</ComboboxBase.Label>
              <div className="group relative grid">
                <ComboboxBase.Input
                  ref={inputRef}
                  className="rounded-b rounded-t-none px-3 py-2 placeholder:text-gray-400"
                  placeholder={placeholder}
                  onBlur={onBlur}
                  onChange={(event) => onInputChange(event.target.value)}
                  onFocus={() => handleInputFocus(open)}
                />

                <div className="absolute inset-y-0 right-2 flex place-items-center">
                  <button
                    type="button"
                    className={cn("invisible p-2", {
                      "group-hover:visible": value,
                    })}
                    onClick={handleClear}
                    tabIndex={-1}
                  >
                    <FaTimes className="text-slate-400 hover:brightness-95" />
                  </button>
                  <ComboboxBase.Button
                    ref={buttonRef}
                    className="p-2"
                    onClick={onOpen}
                  >
                    <FaCaretDown className="text-slate-500 duration-200 hover:brightness-95" />
                  </ComboboxBase.Button>
                </div>
              </div>
            </div>

            <ListBox.Transition>
              <ComboboxBase.Options as={ListBox}>
                {loading && <Loading className="py-2" color="primary" />}
                {!loading && options.length === 0 && (
                  <ListBox.NoOption>{noOptionsText}</ListBox.NoOption>
                )}
                {!loading &&
                  options.map((option) => (
                    <ComboboxBase.Option
                      as={ListBox.Option}
                      key={option}
                      value={option}
                    >
                      {option}
                    </ComboboxBase.Option>
                  ))}
              </ComboboxBase.Options>
            </ListBox.Transition>
          </>
        )}
      </ComboboxBase>
    </div>
  );
};
